import { isPhone } from '@bihu/common-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { RegisterSkinViewModel } from '../../types'

const VERIFY_CODE_LENGTH = 6
const VERIFY_CODE_SECONDS = 60
const DEFAULT_AMOUNT = 10000

type RegisterDialog = 'none' | 'agreement' | 'code'

export interface RegisterSubmission {

  /** 用户在验证码弹窗中填写的六码验证码。 */
  code: string

  /** 已经通过本地格式校验的手机号。 */
  phone: string
}

interface Options {

  /** 注入旧业务容器的短信发送能力；Hook 不直接调用 API。 */
  onRequestCode: (phone: string) => Promise<boolean>

  /** 注入旧业务容器的注册提交能力；Hook 不处理登录后的流程分流。 */
  onSubmitRegister: (submission: RegisterSubmission) => Promise<boolean>
}

export interface RegisterSkinController {

  /** 不含品牌字段的本地展示状态，由皮肤容器补齐品牌后传给 View。 */
  model: Omit<RegisterSkinViewModel, 'brandName' | 'isJhjBrand'>

  /** 更新本地选中的借款金额。 */
  onChangeAmount: (amount: number) => void

  /** 更新验证码并清除上一次验证码错误。 */
  onChangeCode: (code: string) => void

  /** 更新手机号并清除上一次手机号错误。 */
  onChangePhone: (phone: string) => void

  /** 更新本地选中的借款期限。 */
  onChangePeriod: (period: string) => void

  /** 关闭当前本地弹窗，不触发业务副作用。 */
  onCloseDialog: () => void

  /** 在协议确认后勾选协议并继续验证码流程。 */
  onConfirmAgreement: () => Promise<void>

  /** 校验手机号、确认协议并请求旧业务发送验证码。 */
  onRequestCode: () => Promise<void>

  /** 校验验证码后委托旧业务提交注册。 */
  onSubmit: () => Promise<void>

  /** 切换本地协议勾选状态。 */
  onToggleAgreement: () => void

  /** 校验当前手机号并更新本地错误状态。 */
  onValidatePhone: () => void
}

/**
 * useRegisterSkinController — 注册皮肤的本地交互状态机
 * @returns {RegisterSkinController} 注册展示模型与用户意图处理函数
 */
const useRegisterSkinController = ({ onRequestCode, onSubmitRegister }: Options): RegisterSkinController => {
  const [phone, setPhone] = useState('')
  const [phoneError, setPhoneError] = useState('')
  const [checked, setChecked] = useState(false)
  const [amount, setAmount] = useState(DEFAULT_AMOUNT)
  const [period, setPeriod] = useState('6个月')
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState('')
  const [dialog, setDialog] = useState<RegisterDialog>('none')
  const [isSending, setIsSending] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // 状态更新在同一事件周期内尚未生效，使用 ref 阻止自动提交与按钮提交重复请求。
  const isSubmittingRef = useRef(false)

  const validatePhone = useCallback((): boolean => {
    if (!phone) {
      setPhoneError('请输入手机号码')
      return false
    }
    if (!isPhone(phone)) {
      setPhoneError('手机号码格式有误')
      return false
    }

    setPhoneError('')
    return true
  }, [phone])

  const requestCode = useCallback(async(): Promise<boolean> => {
    if (!validatePhone() || isSending || remainingSeconds) {
      return false
    }

    setIsSending(true)
    try {
      const wasSent = await onRequestCode(phone)
      if (wasSent) {
        setRemainingSeconds(VERIFY_CODE_SECONDS)
      }
      return wasSent
    } finally {
      setIsSending(false)
    }
  }, [isSending, onRequestCode, phone, remainingSeconds, validatePhone])

  const openCodeDialog = useCallback(async() => {
    if (!validatePhone()) {
      return
    }
    if (!checked) {
      setDialog('agreement')
      return
    }

    const wasSent = remainingSeconds > 0 || await requestCode()
    if (wasSent) {
      setDialog('code')
    }
  }, [checked, remainingSeconds, requestCode, validatePhone])

  const confirmAgreement = useCallback(async() => {
    setChecked(true)
    setDialog('none')
    const wasSent = remainingSeconds > 0 || await requestCode()
    if (wasSent) {
      setDialog('code')
    }
  }, [remainingSeconds, requestCode])

  const submit = useCallback(async(submittedCode = code) => {
    if (submittedCode.length !== VERIFY_CODE_LENGTH) {
      setCodeError('请输入 6 位验证码')
      return
    }
    if (isSubmittingRef.current) {
      return
    }

    isSubmittingRef.current = true
    setIsSubmitting(true)
    try {
      const wasSubmitted = await onSubmitRegister({
        code: submittedCode,
        phone,
      })
      if (!wasSubmitted) {
        setCodeError('验证码错误，请重新输入')
      }
    } finally {
      isSubmittingRef.current = false
      setIsSubmitting(false)
    }
  }, [code, onSubmitRegister, phone])

  useEffect(() => {
    if (!remainingSeconds) {
      return undefined
    }

    const timer = window.setTimeout(() => {
      setRemainingSeconds(seconds => Math.max(seconds - 1, 0))
    }, 1000)

    return () => window.clearTimeout(timer)
  }, [remainingSeconds])

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        return
      }

      // 下载页返回会恢复注册页 DOM；验证码已完成过注册，关闭弹窗避免恢复的输入值与状态不一致。
      isSubmittingRef.current = false
      setIsSubmitting(false)
      setCode('')
      setCodeError('')
      setDialog('none')
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  return {
    model: {
      amount,
      checked,
      code,
      codeError,
      isSending,
      isSubmitting,
      phone,
      phoneError,
      period,
      remainingSeconds,
      showAgreementDialog: dialog === 'agreement',
      showCodeDialog: dialog === 'code',
    },
    onChangeAmount: setAmount,
    onChangeCode: value => {
      setCode(value)
      setCodeError('')
      if (value.length === VERIFY_CODE_LENGTH) {
        return submit(value)
      }
      return undefined
    },
    onChangePhone: value => {
      setPhone(value)
      setPhoneError('')
    },
    onChangePeriod: setPeriod,
    onCloseDialog: () => setDialog('none'),
    onConfirmAgreement: confirmAgreement,
    onRequestCode: openCodeDialog,
    onSubmit: submit,
    onToggleAgreement: () => setChecked(value => !value),
    onValidatePhone: validatePhone,
  }
}

export default useRegisterSkinController
